import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { can } from '../auth';

function DriverModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error('Name, email and password required');
    setLoading(true);
    try {
      await api.post('/users', { ...form, role: 'driver' });
      toast.success('Driver added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add driver');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-auto my-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-800">Add Driver</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {[['name', 'Full Name *', 'text'], ['email', 'Email *', 'email'], ['phone', 'Phone', 'text'], ['password', 'Password *', 'password']].map(([k, lbl, type]) => (
            <div key={k}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
              <input type={type} value={form[k]} onChange={set(k)}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
            </div>
          ))}
          <button type="submit" disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add Driver'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Drivers() {
  const [showModal, setShowModal] = useState(false);
  const qc = useQueryClient();

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['all-drivers'],
    queryFn: () => api.get('/users/drivers').then((r) => r.data),
  });

  const { data: stats = [] } = useQuery({
    queryKey: ['driver-stats'],
    queryFn: () => api.get('/reports/drivers').then((r) => r.data),
  });

  const statsMap = Object.fromEntries(stats.map((s) => [s.driver_id, s]));

  return (
    <div className="space-y-4">
      {showModal && (
        <DriverModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); qc.invalidateQueries({ queryKey: ['all-drivers'] }); }}
        />
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Drivers</h1>
        {can('system.users') && <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          <Plus size={16} /> Add Driver
        </button>}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-center">Collected (30d)</th>
                  <th className="px-4 py-3 text-center">Delivered (30d)</th>
                  <th className="px-4 py-3 text-center">Failed (30d)</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {drivers.map((d) => {
                  const s = statsMap[d.id] || {};
                  return (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{d.name}</td>
                      <td className="px-4 py-3 text-gray-600">{d.phone || '—'}</td>
                      <td className="px-4 py-3 text-center text-blue-600 font-semibold">{s.collected || 0}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{s.delivered || 0}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-semibold">{s.failed || 0}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${d.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {d.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {drivers.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No drivers yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
